import { useEffect, useRef, useState, type ReactNode } from "react";
import type { CollisionType, Insurer } from "../shared/types";
import {
  COLLISION_TYPES,
  IMPACT_POINTS,
  ROLL_KINDS,
  SIDE_KINDS,
  SYMPTOMS,
  deriveCollisionType,
  followUpsFrom,
  needsRollFollowUp,
  needsSideFollowUp,
} from "../shared/questionnaire";
import {
  ApiError,
  createSession,
  getSession,
  patchSession,
  listInsurers,
  generateEstimate,
  type SessionState,
} from "./api";
import { saveWizard, clearWizard, saveReport, type SavedWizard } from "./persist";
import { Results } from "./Results";
import type { Estimate } from "../shared/types";
import { CONSENT_VERSION } from "../shared/consent";
import { ImpactDiagram, StatBars, CAR_COLORS } from "./GarageCar";
import { GarageView } from "./GarageView";
import { previewStats } from "./rulesPreview";
import { MAKES, OTHER, OTHER_LABEL, YEARS, modelsFor, withCurrent } from "./vehicles";
import type { ImpactPoint, RollKind, SideKind } from "../shared/questionnaire";

interface Form {
  insurerId: string;
  color: string; // garage-visual only, never sent to the server
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  locationZip: string;
  locationLat: number | null;
  locationLng: number | null;
  // The collision type is DERIVED from the impact points plus the two
  // follow-ups below (see collisionTypeOf) — it is never asked directly.
  impactPoints: string[];
  impactUnsure: boolean;
  sideKind: SideKind | "";
  rollKind: RollKind | "";
  symptoms: string[];
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

const EMPTY: Form = {
  insurerId: "",
  color: CAR_COLORS[0],
  vehicleMake: "",
  vehicleModel: "",
  vehicleYear: "",
  locationZip: "",
  locationLat: null,
  locationLng: null,
  impactPoints: [],
  impactUnsure: false,
  sideKind: "",
  rollKind: "",
  symptoms: [],
  contactName: "",
  contactEmail: "",
  contactPhone: "",
};

const STEPS = [
  "Insurer",
  "Vehicle",
  "Location",
  "Damage",
  "Symptoms",
  "Contact",
  "Review",
] as const;

// Index of the "Where did it happen?" step in STEPS — skipped outright when the
// answer already came from the landing garage or the shop finder.
const LOCATION_STEP = 2;

// Rebuild the form from the server's autosaved session — the single source of
// truth on resume. (We deliberately do NOT persist answers locally: that would
// put PII in never-expiring localStorage and let a stale tab clobber newer
// server state. Worst case a resume loses the current step's keystrokes.)
function hydrateForm(s: SessionState): Form {
  return {
    insurerId: s.insurerId ?? "",
    color: CAR_COLORS[0],
    vehicleMake: s.vehicleMake ?? "",
    vehicleModel: s.vehicleModel ?? "",
    vehicleYear: s.vehicleYear !== null ? String(s.vehicleYear) : "",
    locationZip: s.locationZip ?? "",
    locationLat: s.locationLat,
    locationLng: s.locationLng,
    impactPoints: s.impactPoints ?? [],
    // The server stores only the derived type, so read the follow-up answers
    // back out of it rather than persisting them a second time.
    impactUnsure: s.collisionType === "unknown" && (s.impactPoints ?? []).length === 0,
    ...followUpsFrom(s.collisionType ?? "", s.impactPoints ?? []),
    symptoms: s.symptoms ?? [],
    contactName: s.contact?.name ?? "",
    contactEmail: s.contact?.email ?? "",
    contactPhone: s.contact?.phone ?? "",
  };
}

export function Wizard({
  onExit,
  onUnresumable,
  resume,
  prefill,
}: {
  onExit: () => void;
  // The saved session can't be resumed (expired/swept, or already finished).
  // The parent routes the user appropriately — never silently re-consent here.
  onUnresumable: (reason: "expired" | "finished") => void;
  resume?: SavedWizard | null;
  // Answers already given in the shop finder — persisted immediately and the
  // wizard starts past the insurer step.
  prefill?: {
    insurerId: string;
    zip: string | null;
    lat: number | null;
    lng: number | null;
  } | null;
}) {
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Seed prefilled answers from the very first render, so the already-answered
  // insurer step never flashes (and a tap there can't be silently discarded).
  const [form, setForm] = useState<Form>(() =>
    prefill
      ? {
          ...EMPTY,
          insurerId: prefill.insurerId,
          locationZip: prefill.zip ?? "",
          locationLat: prefill.lat,
          locationLng: prefill.lng,
        }
      : EMPTY,
  );
  const [step, setStep] = useState(() => (prefill ? 1 : 0));
  // Location was already given on the landing garage or in the shop finder —
  // asking again is pure friction. Frozen at mount so it can never flip
  // mid-wizard (which would strand the user on a step that vanishes).
  const [skipLocation] = useState(
    () =>
      !resume &&
      !!prefill &&
      (!!prefill.zip || (prefill.lat !== null && prefill.lng !== null)),
  );
  const [saving, setSaving] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [result, setResult] = useState<{ estimate: Estimate; reportId: string } | null>(null);
  const created = useRef(false);
  const booting = useRef(false);
  // Survives a failed prefill PATCH: Try again must reuse this session, not
  // mint another consent-stamped row.
  const createdId = useRef<string | null>(null);

  // Load insurers, then either resume the saved session or create a fresh one.
  // Retryable: a roadside connection blip must never permanently brick the app.
  async function bootstrap() {
    if (booting.current) return; // a slow first attempt must not double-create
    booting.current = true;
    setBootError(null);
    try {
      setInsurers(await listInsurers());
      if (resume) {
        try {
          const { session } = await getSession(resume.sessionId);
          if (session.status === "estimated") {
            // Already finished — the report link (saved on the landing screen)
            // is the thing to show, not a re-run of the wizard.
            clearWizard();
            onUnresumable("finished");
            return;
          }
          setSessionId(resume.sessionId);
          setForm({
            ...hydrateForm(session),
            ...(resume.color && CAR_COLORS.includes(resume.color) ? { color: resume.color } : {}),
          });
          setStep(Math.min(Math.max(resume.step, 0), STEPS.length - 1));
          return;
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) {
            // Genuinely gone (expired/swept). Route back out — a fresh session
            // must go through the consent screen again, not be minted here.
            clearWizard();
            onUnresumable("expired");
            return;
          }
          // Transient failure: keep the saved session so Try again can resume.
          throw e;
        }
      }
      // The wizard only mounts after the safety-screen consent checkbox.
      if (!createdId.current) {
        const s = await createSession(CONSENT_VERSION);
        createdId.current = s.id;
      }
      if (prefill) {
        // Carrier (and maybe ZIP) were answered in the shop finder — persist
        // them now. Non-fatal on failure: the form is already seeded and the
        // per-step autosave persists everything the estimate actually uses.
        const patch: Record<string, unknown> = { insurerId: prefill.insurerId };
        if (prefill.zip) patch.locationZip = prefill.zip;
        if (prefill.lat !== null && prefill.lng !== null) {
          patch.locationLat = prefill.lat;
          patch.locationLng = prefill.lng;
        }
        try {
          await patchSession(createdId.current, patch);
        } catch {
          /* best-effort */
        }
      }
      setSessionId(createdId.current);
    } catch (e) {
      setBootError(String((e as Error).message ?? e));
    } finally {
      booting.current = false;
    }
  }

  useEffect(() => {
    if (created.current) return;
    created.current = true;
    void bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the interrupted-and-return path alive: persist only the session id
  // and step — answers live server-side and rehydrate on resume.
  // Nothing is saved until there's real progress, so an untouched wizard
  // never advertises a "Resume" that resumes nothing.
  useEffect(() => {
    if (!sessionId || result) return;
    if (step === 0 && !form.insurerId) return;
    saveWizard({ sessionId, step, color: form.color });
  }, [sessionId, step, form.insurerId, form.color, result]);

  // Step transitions hop over the location step when we already have it.
  const nextStep = (s: number) => (skipLocation && s + 1 === LOCATION_STEP ? s + 2 : s + 1);
  const prevStep = (s: number) => (skipLocation && s - 1 === LOCATION_STEP ? s - 2 : s - 1);

  const upd = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));
  const toggleSymptom = (id: string) =>
    setForm((f) => ({
      ...f,
      symptoms: f.symptoms.includes(id) ? f.symptoms.filter((x) => x !== id) : [...f.symptoms, id],
    }));

  // Picking a point always cancels "I'm not sure", and dropping the roof drops
  // the rollover answer with it — otherwise a stale "yes, it rolled" would keep
  // deriving a rollover from a car that no longer has a roof hit selected.
  const toggleImpact = (id: string) =>
    setForm((f) => {
      const impactPoints = f.impactPoints.includes(id)
        ? f.impactPoints.filter((x) => x !== id)
        : [...f.impactPoints, id];
      return {
        ...f,
        impactPoints,
        impactUnsure: false,
        rollKind: impactPoints.includes("roof") ? f.rollKind : "",
      };
    });

  const setUnsure = () =>
    setForm((f) =>
      f.impactUnsure
        ? { ...f, impactUnsure: false }
        : { ...f, impactUnsure: true, impactPoints: [], sideKind: "", rollKind: "" },
    );

  // Persist the fields relevant to the current step, then advance.
  async function next() {
    if (!sessionId) return;
    setSaving(true);
    setError(null);
    try {
      const patch = patchForStep(step, form);
      // The skipped location step never patches on its own — fold its fields in
      // here, so the answer still lands if the best-effort prefill PATCH failed.
      if (skipLocation && step === LOCATION_STEP - 1) {
        Object.assign(patch, patchForStep(LOCATION_STEP, form));
      }
      if (Object.keys(patch).length) await patchSession(sessionId, patch);
      if (step === STEPS.length - 1) {
        const r = await generateEstimate(sessionId);
        // The wizard is done; from here the report link is the thing to keep.
        clearWizard();
        saveReport({ reportId: r.reportId, expiresAt: r.expiresAt });
        setResult(r);
      } else {
        setStep(nextStep);
      }
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setSaving(false);
    }
  }

  function captureLocation() {
    if (!("geolocation" in navigator)) {
      setError("Location isn't available on this device. A ZIP code works too.");
      return;
    }
    setGeoBusy(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Round to ~3 decimals (~110 m): plenty for shop distance, and we
        // never store a more precise position than we need.
        upd({
          locationLat: Math.round(pos.coords.latitude * 1000) / 1000,
          locationLng: Math.round(pos.coords.longitude * 1000) / 1000,
        });
        setGeoBusy(false);
      },
      () => {
        setGeoBusy(false);
        setError("Couldn't get your location. A ZIP code works too.");
      },
      { timeout: 10000, maximumAge: 60000 },
    );
  }

  if (result) {
    return (
      <Results
        estimate={result.estimate}
        reportId={result.reportId}
        shopCtx={{
          insurer: insurers.find((i) => i.id === form.insurerId) ?? null,
          zip: form.locationZip || null,
          lat: form.locationLat,
          lng: form.locationLng,
        }}
        selfEmail={form.contactEmail || undefined}
        garageCar={{
          color: form.color,
          insurerName:
            form.insurerId === "uninsured" || form.insurerId === "other"
              ? undefined
              : insurers.find((i) => i.id === form.insurerId)?.name,
          zip: form.locationZip || undefined,
          collisionType: collisionTypeOf(form),
          impactPoints: form.impactPoints,
          symptoms: form.symptoms,
        }}
        onRestart={onExit}
      />
    );
  }

  // Bootstrap failed before a session existed: a dead-end without this.
  if (bootError && !sessionId) {
    return (
      <div className="wizard">
        <div className="card">
          <Field title="Connection problem" hint={bootError}>
            <button className="cta" onClick={() => void bootstrap()}>
              Try again
            </button>
          </Field>
        </div>
        <div className="nav">
          <button className="ghost" onClick={onExit}>Back</button>
        </div>
      </div>
    );
  }

  // Uninsured/other must not render as "insured by ..." — the banner is
  // hidden entirely for them.
  const insurerName =
    form.insurerId === "uninsured" || form.insurerId === "other"
      ? undefined
      : insurers.find((i) => i.id === form.insurerId)?.name;
  const collisionType = collisionTypeOf(form);
  const showStats = step >= 3;
  const preview = showStats
    ? previewStats(collisionType, form.impactPoints, form.symptoms)
    : null;

  return (
    <div className="wizard">
      {/* A skipped step is not counted — "Step 3 of 8", never a number that jumps. */}
      <ProgressBar
        step={step - (skipLocation && step > LOCATION_STEP ? 1 : 0)}
        total={STEPS.length - (skipLocation ? 1 : 0)}
      />
      {error && <p className="err">{error}</p>}

      <div className="garage">
        <GarageView
          car={{
            color: form.color,
            insurerName,
            zip: form.locationZip || undefined,
            collisionType,
            impactPoints: form.impactPoints,
            symptoms: form.symptoms,
          }}
          onToggleImpact={step === 3 ? toggleImpact : undefined}
        />
        {preview && <StatBars preview={preview} />}
      </div>

      <div className="card">
        {step === 0 && (
          <Field title="Who is your insurer?" hint="Not insured? Choose that option and you'll still get a full pricing guide.">
            <div className="options">
              {insurers.map((i) => (
                <button
                  key={i.id}
                  className={`opt ${form.insurerId === i.id ? "sel" : ""}`}
                  onClick={() => upd({ insurerId: i.id })}
                >
                  {i.name}
                </button>
              ))}
              {insurers.length === 0 && <p className="sub">Loading insurers…</p>}
            </div>
          </Field>
        )}

        {step === 1 && (
          <Field title="What vehicle was involved?" hint="Pick from the lists. The spelling has to match our parts pricing, so we don’t ask you to type it.">
            <div className="color-row" role="radiogroup" aria-label="Car color">
              {CAR_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`swatch ${form.color === c ? "sel" : ""}`}
                  style={{ background: c }}
                  aria-label={`Car color ${c}`}
                  onClick={() => upd({ color: c })}
                />
              ))}
            </div>
            <Picker
              label="Make"
              value={form.vehicleMake}
              options={withCurrent(MAKES, form.vehicleMake)}
              onChange={(v) => upd({ vehicleMake: v, vehicleModel: "" })}
            />
            <Picker
              label="Model"
              value={form.vehicleModel}
              options={withCurrent(modelsFor(form.vehicleMake), form.vehicleModel)}
              disabled={!form.vehicleMake}
              disabledLabel="Model (pick a make first)"
              onChange={(v) => upd({ vehicleModel: v })}
            />
            <Picker
              label="Year"
              value={form.vehicleYear}
              options={withCurrent(YEARS, form.vehicleYear)}
              other={false}
              onChange={(v) => upd({ vehicleYear: v })}
            />
          </Field>
        )}

        {step === 2 && (
          <Field title="Where did it happen?" hint="Used only to sort nearby repair shops by distance. Optional.">
            <input className="inp" placeholder="ZIP code" inputMode="numeric" value={form.locationZip} onChange={(e) => upd({ locationZip: e.target.value.replace(/\D/g, "").slice(0, 5) })} />
            <button type="button" className="ghost geo-btn" onClick={captureLocation} disabled={geoBusy}>
              {geoBusy ? "Getting location…" : "Use my current location"}
            </button>
            {form.locationLat !== null && (
              <p className="geo-ok">
                ✓ Location captured. Shops will be sorted by distance.{" "}
                <button
                  type="button"
                  className="geo-clear"
                  onClick={() => upd({ locationLat: null, locationLng: null })}
                >
                  Remove
                </button>
              </p>
            )}
          </Field>
        )}

        {/* One damage question. The collision type used to be asked separately
            here, but for front/rear/driver/passenger it added nothing the
            diagram doesn't already say — so it is derived, and only the two
            distinctions the diagram genuinely can't settle are asked, each
            appearing only when it changes the answer. */}
        {step === 3 && (
          <Field title="Where was the vehicle hit?" hint="Tap the car, or use the buttons. Select everywhere it was hit.">
            <ImpactDiagram
              color={form.color}
              selected={form.impactPoints}
              onToggle={(id: ImpactPoint) => toggleImpact(id)}
            />
            <div className="options">
              {IMPACT_POINTS.map((p) => (
                <button key={p.id} className={`opt ${form.impactPoints.includes(p.id) ? "sel" : ""}`} onClick={() => toggleImpact(p.id)}>
                  {p.label}
                </button>
              ))}
              {/* Escape hatch: this step is now required, so there has to be a
                  way through for someone who genuinely doesn't know. */}
              <button className={`opt ${form.impactUnsure ? "sel" : ""}`} onClick={setUnsure}>
                <strong>I'm not sure</strong>
                <span className="opt-hint">We'll point you at a professional inspection instead</span>
              </button>
            </div>

            {needsRollFollowUp(form.impactPoints) && (
              <div className="followup">
                <h3>Did the vehicle roll or tip over?</h3>
                <div className="options">
                  {ROLL_KINDS.map((r) => (
                    <button key={r.id} className={`opt ${form.rollKind === r.id ? "sel" : ""}`} onClick={() => upd({ rollKind: r.id })}>
                      <strong>{r.label}</strong>
                      {r.hint && <span className="opt-hint">{r.hint}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {needsSideFollowUp(form.impactPoints, form.rollKind) && (
              <div className="followup">
                <h3>Was it a scrape, or a solid hit?</h3>
                <p className="sub">These repair very differently, so it's worth getting right.</p>
                <div className="options">
                  {SIDE_KINDS.map((k) => (
                    <button key={k.id} className={`opt ${form.sideKind === k.id ? "sel" : ""}`} onClick={() => upd({ sideKind: k.id })}>
                      <strong>{k.label}</strong>
                      {k.hint && <span className="opt-hint">{k.hint}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Field>
        )}

        {step === 4 && (
          <Field title="Any of these afterward?" hint="Select all that apply. These help spot hidden damage.">
            <div className="options">
              {SYMPTOMS.map((s) => (
                <button key={s.id} className={`opt ${form.symptoms.includes(s.id) ? "sel" : ""}`} onClick={() => toggleSymptom(s.id)}>
                  {s.label}
                </button>
              ))}
            </div>
          </Field>
        )}

        {step === 5 && (
          <Field title="Want your details on file? (optional)">
            <input className="inp" placeholder="Name (optional)" value={form.contactName} onChange={(e) => upd({ contactName: e.target.value })} />
            <input className="inp" placeholder="Email (optional)" inputMode="email" value={form.contactEmail} onChange={(e) => upd({ contactEmail: e.target.value })} />
            <input className="inp" placeholder="Phone (optional)" inputMode="tel" value={form.contactPhone} onChange={(e) => upd({ contactPhone: e.target.value })} />
          </Field>
        )}

        {step === 6 && (
          <Field title="Review" hint="Check your answers, then get your educational pricing guide.">
            <Review form={form} insurers={insurers} />
          </Field>
        )}
      </div>

      <div className="nav">
        <button className="ghost" onClick={step === 0 ? onExit : () => setStep(prevStep)} disabled={saving}>
          {step === 0 ? "Cancel" : "Back"}
        </button>
        <button className="cta" onClick={next} disabled={saving || geoBusy || !canAdvance(step, form, sessionId)}>
          {saving ? "Saving…" : step === STEPS.length - 1 ? "Get my pricing guide" : "Next"}
        </button>
      </div>
    </div>
  );
}

// --- helpers / subcomponents ---

function patchForStep(step: number, f: Form): Record<string, unknown> {
  switch (step) {
    case 0: return { insurerId: f.insurerId };
    case 1: return { vehicleMake: f.vehicleMake, vehicleModel: f.vehicleModel, vehicleYear: f.vehicleYear ? Number(f.vehicleYear) : null };
    case 2: return { locationZip: f.locationZip || null, locationLat: f.locationLat, locationLng: f.locationLng };
    case 3: return { collisionType: collisionTypeOf(f) || null, impactPoints: f.impactPoints };
    case 4: return { symptoms: f.symptoms };
    case 5: return { contact: { name: f.contactName, email: f.contactEmail, phone: f.contactPhone } };
    default: return {};
  }
}

// The collision type the estimate is built from, derived rather than asked.
// Empty until the damage step has an answer, so the live preview stays blank.
function collisionTypeOf(f: Form): CollisionType | "" {
  if (f.impactUnsure) return "unknown";
  if (f.impactPoints.length === 0) return "";
  return deriveCollisionType(f.impactPoints, f.sideKind, f.rollKind);
}

// Only the insurer and damage steps are required; everything else can be
// skipped so no user ever dead-ends. A follow-up counts as required only while
// it is on screen, and "I'm not sure" is always available as the way through.
function canAdvance(step: number, f: Form, sessionId: string | null): boolean {
  if (!sessionId) return false;
  if (step === 0) return !!f.insurerId;
  if (step === 3) {
    if (!f.impactUnsure && f.impactPoints.length === 0) return false;
    if (needsRollFollowUp(f.impactPoints) && !f.rollKind) return false;
    if (needsSideFollowUp(f.impactPoints, f.rollKind) && !f.sideKind) return false;
  }
  return true;
}

// A dropdown standing in for what used to be a free-text box. `other` is on by
// default so a vehicle missing from the list is never a dead end; the year list
// covers a fixed range and doesn't need it.
function Picker({
  label,
  value,
  options,
  onChange,
  disabled = false,
  disabledLabel,
  other = true,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  disabled?: boolean;
  disabledLabel?: string;
  other?: boolean;
}) {
  return (
    <select
      className={`inp pick${value ? "" : " unset"}`}
      aria-label={`Vehicle ${label.toLowerCase()}`}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{disabled ? (disabledLabel ?? label) : label}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
      {other && <option value={OTHER}>{OTHER_LABEL}</option>}
    </select>
  );
}

function ProgressBar({ step, total }: { step: number; total: number }) {
  return (
    <div className="progress">
      <div className="progress-fill" style={{ width: `${((step + 1) / total) * 100}%` }} />
      <span className="progress-label">Step {step + 1} of {total}</span>
    </div>
  );
}

function Field({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field">
      <h2>{title}</h2>
      {hint && <p className="sub">{hint}</p>}
      <div className="field-body">{children}</div>
    </div>
  );
}

function Review({ form, insurers }: { form: Form; insurers: Insurer[] }) {
  const insurer = insurers.find((i) => i.id === form.insurerId)?.name ?? "—";
  const collision = COLLISION_TYPES.find((c) => c.id === collisionTypeOf(form))?.label ?? "—";
  const impacts = form.impactUnsure
    ? "Not sure"
    : form.impactPoints.map((id) => IMPACT_POINTS.find((p) => p.id === id)?.label).filter(Boolean).join(", ") || "—";
  const symptoms = form.symptoms.map((id) => SYMPTOMS.find((s) => s.id === id)?.label).filter(Boolean).join(", ") || "None noted";
  // "Other" is the pickers' escape hatch, not a name — drop it so the summary
  // reads "2019 Toyota" rather than "2019 Other Other".
  const vehicle =
    [form.vehicleYear, form.vehicleMake, form.vehicleModel].filter((v) => v && v !== OTHER).join(" ") || "—";
  return (
    <ul className="review">
      <li><span>Insurer</span><b>{insurer}</b></li>
      <li><span>Vehicle</span><b>{vehicle}</b></li>
      {/* Coords take precedence over ZIP server-side — mirror that here. */}
      <li><span>Location</span><b>{form.locationLat !== null ? "Current location" : form.locationZip || "—"}</b></li>
      <li><span>Damage</span><b>{impacts}</b></li>
      {/* Derived from the damage answer — shown so the user can check it. */}
      <li><span>Read as</span><b>{collision}</b></li>
      <li><span>Symptoms</span><b>{symptoms}</b></li>
    </ul>
  );
}
